import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../config/axios';
import { toast } from 'sonner';

export default function CartPage() {
  const navigate = useNavigate();
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [promoCode, setPromoCode] = useState('');

  useEffect(() => {
    axiosInstance
      .get('/cart')
      .then((res) => {
        setCart(res.data);
        setLoading(false);
      })
      .catch(() => {
        toast.error('Failed to load cart');
        setCart({ items: [], total: 0 });
        setLoading(false);
      });
  }, []);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);

  const updateQuantity = async (itemId, quantity) => {
    try {
      const res = await axiosInstance.put(`/cart/items/${itemId}`, { quantity });
      setCart(res.data);
    } catch {
      toast.error('Failed to update');
    }
  };

  const removeItem = async (itemId) => {
    try {
      const res = await axiosInstance.delete(`/cart/items/${itemId}`);
      setCart(res.data);
      toast.success('Removed');
    } catch {
      toast.error('Failed to remove');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-sand flex items-center justify-center pb-24">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E54562]"></div>
      </div>
    );
  }

  const subtotal = cart.items.reduce((sum, item) => sum + item.subtotal, 0);
  const serviceFee = 0;
  const total = cart.total;

  return (
    <div className="min-h-screen bg-[#F9FAFB] pb-28 text-[#2B3A67]" style={{ fontFamily: "'Outfit', sans-serif" }}>
      <header className="sticky top-0 z-30 bg-[#F9FAFB]/90 backdrop-blur-md border-b border-gray-100 pt-safe">
        <div className="flex items-center gap-3 px-6 py-5">
          <button
            onClick={() => navigate('/shop')}
            className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm border border-gray-100"
            data-testid="back-button"
          >
            <i className="ph-bold ph-arrow-left text-xl"></i>
          </button>
          <div>
            <h1 className="font-extrabold text-2xl" data-testid="cart-title">Your Cart</h1>
            <p className="text-xs text-gray-400" data-testid="cart-count">{cart.items.length} items ready</p>
          </div>
        </div>
      </header>

      <main className="px-6 pt-6 space-y-6">
        {cart.items.length === 0 ? (
          <div className="text-center py-12">
            <i className="ph-duotone ph-shopping-cart text-6xl text-gray-300 mb-4"></i>
            <p data-testid="cart-empty" className="text-gray-400 mb-4">Your cart is empty</p>
            <button
              data-testid="continue-shopping-button"
              onClick={() => navigate('/shop')}
              className="btn-primary"
            >
              Continue Shopping
            </button>
          </div>
        ) : (
          <>
            <div className="space-y-4">
              {cart.items.map((item, index) => (
                <div
                  key={item.id}
                  data-testid={`cart-item-${index}`}
                  className="bg-white rounded-2xl p-4 shadow-md border border-gray-100"
                >
                  <div className="flex gap-4">
                    <img
                      data-testid={`cart-item-image-${index}`}
                      src={item.product_image}
                      alt={item.product_name}
                      className="w-20 h-20 object-cover rounded-xl"
                    />
                    <div className="flex-1">
                      <h3 data-testid={`cart-item-name-${index}`} className="font-bold">
                        {item.product_name}
                      </h3>
                      <p data-testid={`cart-item-variant-${index}`} className="text-xs text-gray-400">
                        {item.variant_details}
                      </p>
                      <p data-testid={`cart-item-price-${index}`} className="font-bold text-[#E54562] mt-2">
                        {formatCurrency(item.price)}
                      </p>
                    </div>
                    <button
                      data-testid={`remove-item-${index}`}
                      onClick={() => removeItem(item.id)}
                      className="text-[#E54562]"
                    >
                      <i className="ph-bold ph-trash text-lg"></i>
                    </button>
                  </div>

                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-dashed border-gray-200">
                    <div className="flex items-center gap-3">
                      <button
                        data-testid={`decrease-${item.id}`}
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        disabled={item.quantity === 1}
                        className="w-8 h-8 rounded-full border-2 border-gray-200 text-[#2B3A67] font-bold"
                      >
                        -
                      </button>
                      <span data-testid={`quantity-${item.id}`} className="font-bold w-6 text-center">
                        {item.quantity}
                      </span>
                      <button
                        data-testid={`increase-${item.id}`}
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        className="w-8 h-8 rounded-full border-2 border-gray-200 text-[#2B3A67] font-bold"
                      >
                        +
                      </button>
                    </div>
                    <p data-testid={`cart-item-subtotal-${index}`} className="font-bold">
                      {formatCurrency(item.subtotal)}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-white rounded-2xl p-5 shadow-md border border-gray-100">
              <p className="text-xs uppercase tracking-widest text-gray-400 font-bold mb-3">Promo Code</p>
              <div className="flex gap-2">
                <input
                  data-testid="cart-promo-input"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value)}
                  placeholder="Enter code"
                  className="flex-1 border-2 border-dashed border-gray-200 rounded-xl px-4 py-2 text-sm focus:outline-none"
                />
                <button
                  data-testid="cart-promo-apply"
                  className="px-4 py-2 rounded-xl bg-[#2B3A67] text-white font-bold text-sm"
                >
                  Apply
                </button>
              </div>
            </div>

            <div className="bg-[#2B3A67] rounded-2xl p-6 text-white shadow-lg" data-testid="cart-summary">
              <div className="flex justify-between text-sm mb-2">
                <span>Subtotal</span>
                <span data-testid="cart-subtotal">{formatCurrency(subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm mb-4">
                <span>Service fee</span>
                <span data-testid="cart-service-fee">{formatCurrency(serviceFee)}</span>
              </div>
              <div className="flex justify-between font-extrabold text-lg mb-6">
                <span>Total</span>
                <span data-testid="cart-total">{formatCurrency(total)}</span>
              </div>
              <button
                data-testid="checkout-button"
                onClick={() => navigate('/checkout')}
                className="w-full py-4 rounded-xl bg-[#E54562] font-extrabold text-white"
              >
                Proceed to Checkout
              </button>
            </div>
          </>
        )}
      </main>
    </div>
  );
}